/**
 * register-page.tsx — Khách doanh nghiệp tự đăng ký (/register, /signup).
 *
 * Bước 1: form (họ tên, email, SĐT, tên doanh nghiệp, mật khẩu, mã giới thiệu, đồng ý điều khoản) → server gửi mã 6 số.
 * Bước 2: nhập mã (gửi lại có đếm ngược, đổi email) → tạo tổ chức gói Free + tài khoản chủ sở hữu → vào hệ thống.
 * Google: "Tiếp tục với Google" → tài khoản mới cần "Hoàn tất đăng ký" (tên doanh nghiệp, SĐT); đã có → đăng nhập luôn.
 */
import { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { ArrowLeft, Loader2, MailCheck } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/misc'
import { Loading } from '@/components/shared/feedback'
import { queryClient } from '@/lib/query-client'
import { useAuthStore } from '@/stores/auth-store'
import { markWelcomePending } from '@/lib/welcome'
import { captchaPendingMessage, withTurnstileToken } from '@/lib/turnstile'
import { TurnstileBox, useTurnstile } from '@/components/shared/turnstile-box'
import { AUTH_INPUT, AuthLayout, FieldError } from './auth-layout'
import { authApi, googleSignupFromState, resolveGoogleClientId, toApiErr, useSignupConfig, type SignupConfig } from './auth-api'
import {
  EMPTY_SIGNUP_FORM, emailFromHash, maskEmail, normalizeEmail, readRefPrefill, ticketFor, validateSignupForm,
  type SignupErrors, type SignupForm,
} from './auth-helpers'
import { SocialRow } from './google-button'
import { OtpInput, ResendButton } from './otp-parts'
import { PasswordInput, StrengthMeter } from './password-field'

type Step =
  | { kind: 'form' }
  | { kind: 'otp'; email: string; resendAt: number; devMode: boolean }
  | { kind: 'google'; signupToken: string; email: string }

export function RegisterPage() {
  const { t } = useTranslation('authPages')
  const location = useLocation()
  const { config, loading } = useSignupConfig()
  const prefill = useMemo(() => readRefPrefill(location.search), [location.search])
  const fromLogin = useMemo(() => googleSignupFromState(location.state), [location.state])
  const [form, setForm] = useState<SignupForm>(() => ({
    ...EMPTY_SIGNUP_FORM, referralCode: prefill?.code ?? '', fullName: fromLogin?.fullName ?? '', email: fromLogin?.email ?? emailFromHash(location.hash),
  }))
  const [errors, setErrors] = useState<SignupErrors>({})
  const [step, setStep] = useState<Step>(() =>
    fromLogin ? { kind: 'google', signupToken: fromLogin.signupToken, email: fromLogin.email } : { kind: 'form' })
  const applySession = useAuthStore((s) => s.applySession)

  const set = <K extends keyof SignupForm>(k: K, v: SignupForm[K]) => {
    setForm((f) => ({ ...f, [k]: v }))
    setErrors((e) => ({ ...e, [k]: undefined }))
  }

  // `isNew`: tài khoản vừa được tạo (OTP email / hoàn tất Google) → popup chào mừng kèm gói Free ở trang đầu tiên.
  // Google nhận ra tài khoản có sẵn (logged_in) chỉ là đăng nhập → không chào lại.
  const onSession = useCallback((data: Parameters<typeof applySession>[0], isNew = true) => {
    queryClient.removeQueries({ queryKey: ['me', 'permissions'] })
    if (isNew) markWelcomePending(data.user.id)
    applySession(data)
    if (!isNew) toast.success(t('register.loginSuccess'))
  }, [applySession, t])

  const onGoogle = useCallback(async (credential: string) => {
    try {
      const r = await authApi.google(credential)
      if (r.status === 'totp_required') {
        // Tài khoản có sẵn bật / bắt buộc xác thực 2 lớp — nhập mã ở trang Đăng nhập (không có ô nhập mã ở đây).
        toast.info(t('register.totpRequiredInfo'))
        return
      }
      if (r.status === 'logged_in') {
        onSession(r, false)
        return
      }
      setForm((f) => ({ ...f, fullName: f.fullName || r.profile.fullName, email: r.profile.email }))
      setErrors({})
      setStep({ kind: 'google', signupToken: r.signupToken, email: r.profile.email })
    } catch (err) {
      toast.error(toApiErr(err).message)
    }
  }, [onSession, t])

  if (loading) return <AuthLayout title={t('register.freeTitle')}><Loading label={t('register.loadingLabel')} /></AuthLayout>
  if (config && !config.enabled) return <SignupClosed config={config} />

  const googleClientId = resolveGoogleClientId(config)
  const footer = (
    <>
      {t('register.haveAccount')} <Link to={`/login${location.search}`} className="font-medium text-primary hover:underline">{t('register.login')}</Link>
    </>
  )

  if (step.kind === 'otp') {
    return (
      <AuthLayout
        title={t('register.verifyEmailTitle')}
        subtitle={<Trans t={t} i18nKey="register.verifyEmailSubtitle" values={{ email: maskEmail(step.email) }} components={{ b: <b className="text-foreground" /> }} />}
        footer={footer}
      >
        <OtpStep
          step={step}
          onBack={() => setStep({ kind: 'form' })}
          onSession={onSession}
          onResent={(resendInSec, devMode) => setStep({ ...step, resendAt: Date.now() + resendInSec * 1000, devMode })}
        />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title={step.kind === 'google' ? t('register.completeTitle') : t('register.freeTitle')}
      subtitle={step.kind === 'google'
        ? <Trans t={t} i18nKey="register.googleSubtitle" values={{ email: step.email }} components={{ b: <b className="text-foreground" /> }} />
        : t('register.freeSubtitle')}
      footer={footer}
    >
      <SignupFormView
        form={form}
        errors={errors}
        setErrors={setErrors}
        set={set}
        config={config}
        google={step.kind === 'google'}
        onCancelGoogle={() => setStep({ kind: 'form' })}
        onSubmit={async (turnstileToken) => {
          const payload = {
            fullName: form.fullName.trim(),
            phone: form.phone.trim(),
            orgName: form.orgName.trim(),
            acceptTerms: form.acceptTerms,
            referralCode: form.referralCode.trim() || null,
            refTicket: ticketFor(form.referralCode, prefill),
          }
          if (step.kind === 'google') {
            onSession(await authApi.googleComplete({ ...payload, signupToken: step.signupToken }))
            return
          }
          const email = normalizeEmail(form.email)
          const r = await authApi.registerStart(withTurnstileToken({ ...payload, email, password: form.password, website: form.website }, turnstileToken))
          setStep({ kind: 'otp', email: r.email || email, resendAt: Date.now() + r.resendInSec * 1000, devMode: !!r.devMode })
          toast.success(t('register.verifySentToast'))
        }}
      />
      {step.kind === 'form' && <SocialRow clientId={googleClientId} onCredential={onGoogle} context="signup" />}
    </AuthLayout>
  )
}

function SignupFormView({ form, errors, setErrors, set, config, google, onSubmit, onCancelGoogle }: {
  form: SignupForm
  errors: SignupErrors
  setErrors: (e: SignupErrors) => void
  set: <K extends keyof SignupForm>(k: K, v: SignupForm[K]) => void
  config: SignupConfig | null
  google: boolean
  /** `turnstileToken`: token Cloudflare Turnstile (null khi chưa bật / đang hoàn tất đăng ký Google — Google đã là bằng chứng người thật). */
  onSubmit: (turnstileToken: string | null) => Promise<void>
  onCancelGoogle: () => void
}) {
  const { t } = useTranslation('authPages')
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const ts = useTurnstile(google ? null : config?.turnstileSiteKey, 'register')
  const termsUrl = config?.termsUrl || '/dieu-khoan'
  const privacyUrl = config?.privacyUrl || '/chinh-sach-bao-mat'

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)
    const errs = validateSignupForm(form, { google })
    setErrors(errs)
    if (Object.keys(errs).length) return
    if (ts.pending) return setFormError(captchaPendingMessage())
    setBusy(true)
    try {
      await onSubmit(ts.token)
    } catch (err) {
      ts.reset() // token Turnstile dùng được một lần — backend kiểm nó TRƯỚC khi kiểm form, nên lỗi nào cũng đã tiêu token
      const a = toApiErr(err)
      if (a.fields) setErrors(a.fields as SignupErrors)
      else if (a.field) setErrors({ [a.field]: a.message } as SignupErrors)
      if (a.code === 'EMAIL_TAKEN' || !a.field) setFormError(a.message)
      if (a.code === 'GOOGLE_SIGNUP_EXPIRED') onCancelGoogle()
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3.5" noValidate>
      <div className="grid gap-3.5 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="su-name">{t('register.form.fullName')}</Label>
          <Input id="su-name" className={AUTH_INPUT} value={form.fullName} onChange={(e) => set('fullName', e.target.value)} autoComplete="name" maxLength={120} />
          <FieldError msg={errors.fullName} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="su-phone">{t('register.form.phone')}</Label>
          <Input id="su-phone" className={AUTH_INPUT} value={form.phone} onChange={(e) => set('phone', e.target.value)} inputMode="tel" autoComplete="tel" placeholder={t('register.form.phonePlaceholder')} maxLength={20} />
          <FieldError msg={errors.phone} />
        </div>
      </div>
      {!google && (
        <div className="space-y-1.5">
          <Label htmlFor="su-email">{t('register.form.email')}</Label>
          <Input id="su-email" className={AUTH_INPUT} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} autoComplete="email" placeholder={t('register.form.emailPlaceholder')} maxLength={160} />
          <FieldError msg={errors.email} />
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="su-org">{t('register.form.orgName')}</Label>
        <Input id="su-org" className={AUTH_INPUT} value={form.orgName} onChange={(e) => set('orgName', e.target.value)} autoComplete="organization" maxLength={200} />
        <FieldError msg={errors.orgName} />
      </div>
      {!google && (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="su-pass">{t('register.form.password')}</Label>
            <PasswordInput id="su-pass" value={form.password} onChange={(e) => set('password', e.target.value)} autoComplete="new-password" maxLength={72} />
            {form.password && <StrengthMeter password={form.password} />}
            <FieldError msg={errors.password} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="su-pass2">{t('register.form.confirmPassword')}</Label>
            <PasswordInput id="su-pass2" value={form.confirmPassword} onChange={(e) => set('confirmPassword', e.target.value)} autoComplete="new-password" maxLength={72} />
            <FieldError msg={errors.confirmPassword} />
          </div>
        </>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="su-ref">{t('register.form.referralCode')} <span className="font-normal text-muted-foreground">{t('register.form.referralOptional')}</span></Label>
        <Input id="su-ref" className={`${AUTH_INPUT} uppercase`} value={form.referralCode} onChange={(e) => set('referralCode', e.target.value)} maxLength={20} autoComplete="off" />
        <FieldError msg={errors.referralCode} />
      </div>
      {/* honeypot chống bot — người thật không thấy ô này */}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={form.website} onChange={(e) => set('website', e.target.value)} />
      <div className="space-y-1">
        <label className="flex items-start gap-2 text-sm">
          <Checkbox className="mt-0.5" checked={form.acceptTerms} onCheckedChange={(v) => set('acceptTerms', v === true)} aria-label={t('register.form.acceptTermsAria')} />
          <span>
            {t('register.form.agreePrefix')}{' '}
            <a href={termsUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">{t('register.form.termsOfUse')}</a>
            {' '}{t('register.form.and')}{' '}
            <a href={privacyUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">{t('register.form.privacyPolicy')}</a>
          </span>
        </label>
        <FieldError msg={errors.acceptTerms} />
      </div>
      {formError && (
        <p className="rounded-md bg-destructive/10 p-2.5 text-sm text-destructive">
          {formError}
          {formError.includes('Email đã được dùng') /* i18n-ignore — so khớp thông điệp lỗi backend trả về, không phải nhãn UI */ && (
            <> <Link to="/login" className="font-medium underline">{t('register.form.loginShort')}</Link> · <Link to="/forgot-password" className="font-medium underline">{t('register.form.forgotPassword')}</Link></>
          )}
        </p>
      )}
      <TurnstileBox ts={ts} />
      <Button type="submit" className="h-12 w-full rounded-lg text-base" disabled={busy || ts.pending}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        {google ? t('register.form.submitComplete') : t('register.form.submitRegister')}
      </Button>
      {google && (
        <button type="button" className="w-full text-center text-sm text-muted-foreground hover:text-foreground" onClick={onCancelGoogle}>
          {t('register.form.useOtherEmail')}
        </button>
      )}
    </form>
  )
}

function OtpStep({ step, onBack, onSession, onResent }: {
  step: Extract<Step, { kind: 'otp' }>
  onBack: () => void
  onSession: (d: Awaited<ReturnType<typeof authApi.registerVerify>>) => void
  onResent: (resendInSec: number, devMode: boolean) => void
}) {
  const { t } = useTranslation('authPages')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const verify = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (code.length !== 6) return setError(t('register.otp.needAllDigits'))
    setBusy(true)
    setError(null)
    try {
      onSession(await authApi.registerVerify(step.email, code))
    } catch (err) {
      const a = toApiErr(err)
      setError(a.message)
      if (a.code === 'OTP_LOCKED' || a.code === 'OTP_EXPIRED') setCode('')
      if (a.code === 'SIGNUP_SESSION_EXPIRED') onBack()
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={verify} className="space-y-4">
      <div className="flex items-center gap-3 rounded-lg bg-primary/5 p-3 text-sm">
        <MailCheck className="h-5 w-5 shrink-0 text-primary" />
        <span><Trans t={t} i18nKey="register.otp.checkSpam" components={{ b: <b /> }} /></span>
      </div>
      {step.devMode && (
        <p className="rounded-md border border-dashed p-2.5 text-xs text-muted-foreground">
          <Trans t={t} i18nKey="register.otp.devHint" components={{ code: <code /> }} />
        </p>
      )}
      <OtpInput value={code} onChange={(v) => { setCode(v); setError(null) }} disabled={busy} />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button type="submit" className="h-12 w-full rounded-lg text-base" disabled={busy || code.length !== 6}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t('register.otp.verifyAndCreate')}
      </Button>
      <div className="flex items-center justify-between">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t('register.otp.changeEmail')}
        </button>
        <ResendButton
          until={step.resendAt}
          onResend={async () => {
            try {
              const r = await authApi.registerResend(step.email)
              setCode('')
              setError(null)
              onResent(r.resendInSec, !!r.devMode)
              toast.success(t('register.otp.resentToast'))
            } catch (err) {
              const a = toApiErr(err)
              if (a.retryAfter) onResent(a.retryAfter, step.devMode)
              toast.error(a.message)
              if (a.code === 'SIGNUP_SESSION_EXPIRED') onBack()
            }
          }}
        />
      </div>
    </form>
  )
}

function SignupClosed({ config }: { config: SignupConfig }) {
  const { t } = useTranslation('authPages')
  return (
    <AuthLayout
      title={t('register.signupClosed.title')}
      subtitle={<Trans t={t} i18nKey="register.signupClosed.subtitle" values={{ contact: config.supportContact || t('register.signupClosed.defaultContact') }} />}
      footer={<>{t('register.haveAccount')} <Link to="/login" className="font-medium text-primary hover:underline">{t('register.login')}</Link></>}
    >
      <Button asChild className="h-12 w-full rounded-lg" variant="outline">
        <Link to="/login">{t('register.signupClosed.backToLogin')}</Link>
      </Button>
    </AuthLayout>
  )
}
